import * as React from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button, Form, Icon, Message, Modal } from "semantic-ui-react";
import { Auth } from "../../store/actions/";
import "./SessionExpiredModal.scss";

type RootState = {
  auth: {
    sessionExpired: boolean;
    reauthenticating: boolean;
    reauthError: string | null;
    user: { _id: string; email: string } | null;
  };
  config: {
    baseDomain: string;
    port: string;
  };
};

const SessionExpiredModal: React.FC = () => {
  const dispatch = useDispatch();
  const { sessionExpired, reauthenticating, reauthError, user } = useSelector(
    ({ auth }: RootState) => ({
      sessionExpired: auth.sessionExpired,
      reauthenticating: auth.reauthenticating,
      reauthError: auth.reauthError,
      user: auth.user,
    })
  );
  const { baseDomain, port } = useSelector(({ config }: RootState) => ({
    baseDomain: config.baseDomain,
    port: config.port,
  }));

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);

  // Pre-fill email from existing user in store
  React.useEffect(() => {
    if (sessionExpired && user?.email) {
      setEmail(user.email);
    }
  }, [sessionExpired, user]);

  // Reset password and visibility when modal opens
  React.useEffect(() => {
    if (sessionExpired) {
      setPassword("");
      setShowPassword(false);
    }
  }, [sessionExpired]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password || reauthenticating) return;
    dispatch(
      Auth.authReauthApp({
        email,
        password,
        userId: user?._id,
        dispatch,
      })
    );
  };

  const handleGoToWebsite = () => {
    const websiteUrl = `${baseDomain}${port ? ":3000" : ""}/signin`;
    window.location.replace(websiteUrl);
  };

  if (!sessionExpired) return null;

  return (
    <Modal
      open={true}
      size="tiny"
      dimmer="blurring"
      closeOnDimmerClick={false}
      closeOnEscape={false}
      className="session-expired-modal"
    >
      <Modal.Header>Session Expired</Modal.Header>
      <Modal.Content>
        <p className="session-expired-desc">
          Your session has expired. Please sign in again to continue working.
        </p>
        {reauthError && (
          <Message negative className="session-expired-error">
            <Message.Header>Sign in failed</Message.Header>
            <p>{reauthError}</p>
          </Message>
        )}
        <Form onSubmit={handleSubmit} loading={reauthenticating}>
          <Form.Input
            label="Email"
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(_, { value }) => setEmail(value)}
            disabled={reauthenticating}
            required
          />
          <Form.Input
            label="Password"
            type={showPassword ? "text" : "password"}
            placeholder="Enter your password"
            value={password}
            onChange={(_, { value }) => setPassword(value)}
            disabled={reauthenticating}
            required
            icon={
              <Icon
                name={showPassword ? "eye slash" : "eye"}
                link
                className="session-expired-eye-icon"
                onClick={() => setShowPassword(!showPassword)}
              />
            }
          />
          <Button
            type="submit"
            fluid
            loading={reauthenticating}
            disabled={!email || !password || reauthenticating}
            className="session-expired-submit-btn"
          >
            Sign In
          </Button>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <button
          className="session-expired-website-link"
          onClick={handleGoToWebsite}
          disabled={reauthenticating}
        >
          Go to Website
        </button>
      </Modal.Actions>
    </Modal>
  );
};

export default SessionExpiredModal;
